import { FieldTypes } from '../../constants/FieldTypes'
import { DropdownField, Field, FieldMap, MultipleSelectField } from '../../types/forms/fields'
import { getBooleanHistoryField, getPleaseSpecifyField } from '../shared'
import { getConditionOrDiseaseFields, getDetailsField } from './shared'

const nutritionAndExerciseField: FieldMap = {
  name: 'Nutrition and Exercise',
  children: {
    sufficientNutrition: getBooleanHistoryField(
      'In the past 12 months, have you worried that you or your family may not have enough food to eat?',
      true,
    ),
    sufficientNutritionNotes: getDetailsField('sufficientNutrition'),
    qualifiesForFoodAssistance: getBooleanHistoryField(
      'Do you qualify for a food assistance program like SNAP or WIC?',
      true,
    ),
    weightChange: getBooleanHistoryField(
      'During adulthood, have you gained or lost over 20 pounds, unrelated to a pregnancy?',
      true,
    ),
    weightChangeNotes: getDetailsField(
      'weightChange',
      'Please share any concerns regarding your weight or details around fluctuations in weight',
      true,
    ),
    ...getConditionOrDiseaseFields(
      'eatingDisorder',
      'Do you currently have or have you had an eating disorder',
      [
        { text: 'Anorexia nervosa', id: 'anorexia-nervosa' },
        { text: 'Bulimia nervosa', id: 'bulimia-nervosa' },
        { text: 'Binge eating', id: 'binge-eating' },
        { text: 'Avoidant/restrictive food intake disorder', id: 'avoidant-food-intake' },
        { text: 'Rumination disorder', id: 'rumination-disorder' },
        { text: 'Other', id: 'other' },
      ],
      'Do you still struggle with the above, or do you have any other concerns about your eating habits?',
    ),
    ...getConditionOrDiseaseFields(
      'dietaryRestrictions',
      'Do you have any dietary restrictions or concerns?',
      [
        { text: 'Vegetarian', id: 'vegetarian' },
        { text: 'Vegan', id: 'vegan' },
        { text: 'Gluten-free', id: 'gluten-free' },
        { text: 'Dairy-free', id: 'dairy-free' },
        {
          text: 'Lack of access to food preparation/cooking facilities',
          id: 'lack-access-food-prep',
        },
        { text: 'Lack of access to healthy food', id: 'lack-access-healthy-food' },
        { text: 'Other', id: 'other' },
      ],
      'Please share any details about your dietary restrictions, goals, or concerns',
    ),
    ...getConditionOrDiseaseFields(
      'exerciseRestrictions',
      'Do you have any exercise restrictions or concerns?',
      [
        { text: 'Pain or discomfort', id: 'pain-or-discomfort' },
        { text: 'Health concerns', id: 'health-concerns' },
        { text: 'Experience/knowledge', id: 'experience-knowledge' },
        {
          text: 'Lack of access to outdoor excersise',
          id: 'lack-access-outdoor-exercise',
        },
        {
          text: 'Lack of access to indoor excersise',
          id: 'lack-access-indoor-exercise',
        },
        { text: 'Other', id: 'other' },
      ],
      'Please share any details about your exercise restrictions, goals, or concerns',
    ),
  },
}

const getUsePeriodField = (id: string, name: string): Field => ({
  type: FieldTypes.DROPDOWN,
  placeholder: `History of ${name} use`,
  inputType: 'radio',
  options: [
    {
      text: 'Currently using',
      id: 'current',
    },
    {
      text: 'Used in the past',
      id: 'past',
    },
  ],
  condition: v => !!v?.[`${id}Use`],
})

const getUseFrequencyField = (id: string, name: string): Field => ({
  type: FieldTypes.DROPDOWN,
  placeholder: `How often do you / did you use ${name}?`,
  inputType: 'radio',
  options: [
    {
      text: '4 or more times per week',
      id: 'fourOrMoreTimesPerWeek',
    },
    {
      text: '2-3 times per week',
      id: 'twoOrThreeTimesPerWeek',
    },
    {
      text: '2-4 times per month',
      id: 'twoOrFourTimesPerMonth',
    },
    {
      text: 'Monthly or less',
      id: 'monthlyOrLess',
    },
  ],
  condition: v => !!v?.[`${id}Use`],
})

const getDrugUseFields = (
  id: string,
  name: string,
  index: number,
  examples?: string[],
  withTypeOfProductDetails?: boolean,
  typeOfProductExamples?: string[],
): Record<string, Field> => {
  const fields: Record<string, Field> = {
    [`${id}Use`]: getBooleanHistoryField(
      `Do you or have you used ${name}?${examples ? ` (e.g. ${examples.join(', ')})` : ''}`,
      true,
    ),
    [`${id}UsePeriod`]: getUsePeriodField(id, name),
    [`${id}UseFrequency`]: getUseFrequencyField(id, name),
  }
  if (withTypeOfProductDetails) {
    fields[`${id}TypeOfProduct`] = getDetailsField(
      `${id}Use`,
      `What type/form of ${name} did you use?${typeOfProductExamples ? ` (e.g. ${typeOfProductExamples.join(', ')})` : ''}`,
    )
  }
  fields[`${id}Notes`] = {
    type: FieldTypes.TEXTAREA,
    placeholder: `Are there any other details you would like to share about your use of ${name}?`,
    condition: v => !!v?.[`${id}Use`],
    optional: true,
  }
  return fields
}

const alcoholAndDrugUseField: FieldMap = {
  name: 'Alcohol and Drugs',
  children: {
    ...getDrugUseFields(
      'nicotine',
      'tobacco or nicotine products',
      0,
      ['cigarettes', 'vaping'],
      true,
    ),
    ...getDrugUseFields('alcohol', 'alcohol', 1),
    ...getDrugUseFields('marijuana', 'marijuana', 2, undefined, true, [
      'smoking',
      'vaping',
      'edibles',
      'topical',
    ]),
    ...getDrugUseFields('opiates', 'opiates', 3, [
      'hydrocodone',
      'oxycodone',
      'fentanyl',
      'morphine',
    ]),
    ...getDrugUseFields('otherRecreactionalDrugs', 'other recreational drugs', 4, [
      'heroin',
      'methamphetamine',
      'cocaine',
    ]),
    participatedInTreatment: getBooleanHistoryField(
      'Have you participated in any treatment for your alcohol or drug use?',
    ),
    participatedInTreatmentNotes: getPleaseSpecifyField('participatedInTreatment', true),
    causesProblems: getBooleanHistoryField(
      'Does your alcohol or drug use cause problems in your life?',
    ),
    causesProblemsNotes: getPleaseSpecifyField('causesProblems', true),
    friendOrFamilyConcerns: getBooleanHistoryField(
      'Have friends or family expressed concerns about your alcohol or drug use?',
    ),
    friendOrFamilyConcernsNotes: getPleaseSpecifyField('friendOrFamilyConcerns', true),
    wantsToDiscussTreatment: getBooleanHistoryField(
      // 'Do you want to discuss treatment strategies and/or options with your provider?',
      'Would you like to discuss lowering or eliminating your alcohol or drug use with your provider?',
    ),
    wantsToDiscussTreatmentNotes: getPleaseSpecifyField('wantsToDiscussTreatment', true),
  },
}

const childhoodAdversityField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'As a child, have you experience any of the following?',
  optional: true,
  options: [
    {
      text: 'Emotional abuse or neglect',
      id: 'emotionalAbuseOrNeglect',
    },
    {
      text: 'Physical abuse or neglect',
      id: 'physicalAbuseOrNeglect',
    },
    {
      text: 'Sexual abuse or coercion',
      id: 'sexualAbuseOrCoercion',
    },
    {
      text: 'Unstable housing or accesss to utilities',
      id: 'unstableHousing',
    },
    {
      text: 'Lack of food or other basic needs',
      id: 'lackOfFoodOrBasicNeeds',
    },
    {
      text: 'Been coerced or threatened by any family member or other person to do something illegal or harmful',
      id: 'illegalOrHarmfulCoercion',
    },
    {
      text: 'Witnessed abuse, neglect, or coercion BY a family member',
      id: 'witnessedActionByFamilyMember',
    },
    {
      text: 'Witnessed abuse, neglect, or coercion OF a family member',
      id: 'witnessedActionOfFamilyMember',
    },
    { text: 'Parents incarcerated for any length of time', id: 'parentsIncarcerated' },
    { text: 'Bullying or harassment', id: 'bullyingOrHarassment' },
  ],
}

const feelsSafeWithPartnerField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Do you feel safe with your current partner or significant other?',
  inputType: 'radio',
  options: [
    { text: 'Always', id: 'always' },
    { text: 'Most of the time', id: 'mostOfTheTime' },
    { text: 'Sometimes', id: 'sometimes' },
    { text: 'Rarely', id: 'rarely' },
    { text: 'Never', id: 'never' },
    { text: 'I do not have a partner or significant other', id: 'noPartner' },
  ],
}

const safetyAndRelationshipField: FieldMap = {
  name: 'Safety and Relationships',
  children: {
    childhoodAdversity: childhoodAdversityField,
    childhoodAdversityNotes: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Are there any other details you would like to share about your childhood?',
      optional: true,
    },
    threatenedOrCoerecedByPartner: getBooleanHistoryField(
      'Have you ever felt coerced into illegal or harmful actions by a partner or significant other?',
    ),
    threatenedOrCoerecedByPartnerNotes: getDetailsField(
      'threatenedOrCoerecedByPartner',
      'Please specify whether this behavior is ongoing, or if it happened in the past. Do you feel this affects your health or well-being? Please provide any details you wish to share.',
    ),
    wouldShowSignsOfAbuse: getBooleanHistoryField(
      'If you were afraid or being threatened in a relationship with a partner or significant other, would you tell anyone close to you?',
    ),
    wouldShowSignsOfAbuseNotes: getDetailsField(
      'wouldShowSignsOfAbuse',
      'Please provide any details you wish to share.',
    ),
    sexualAbuseOrCoercion: getBooleanHistoryField(
      'Have you ever been physically abused, attacked (e.g. raped, held down, etc.), causing physical or emotional/psychological harm to you?',
    ),
    sexualAbuseOrCoercionNotes: getDetailsField(
      'sexualAbuseOrCoercion',
      'Please indicate if this has happened recently. Does this affect your health or well-being? Please provide any details you wish to share.',
    ),
    verbalOrEmotionalAbuse: getBooleanHistoryField(
      'Have you ever been verbally or emotionally abused by a partner or significant other?',
    ),
    verbalOrEmotionalAbuseNotes: getDetailsField(
      'verbalOrEmotionalAbuse',
      'Please indicate if this has happened recently. Does this affect your health or well-being? Please provide any details you wish to share.',
    ),
    feelsSafeWithPartner: feelsSafeWithPartnerField,
  },
}

export const socialHistoryStage: FieldMap = {
  name: 'Social History',
  children: {
    nutritionAndExercise: nutritionAndExerciseField,
    alcoholAndDrugUse: alcoholAndDrugUseField,
    safetyAndRelationship: safetyAndRelationshipField,
  },
}
